import {Helmet, HelmetProvider} from '@dr.pogodin/react-helmet';
import isPropValid from '@emotion/is-prop-valid';
import {NuqsAdapter} from 'nuqs/adapters/react-router/v7';
import {DynamicUiProvider} from 'platform/components';
import {Box} from 'platform/foundation';
import {StyleSheetManager} from 'styled-components';

import {Provider} from 'react-redux';
import {
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom';

import i18n from '@dms/i18n';
import {
  doc360Routes,
  iamRedirectRoute,
  microsoftOAuthFinish,
  microsoftOAuthRedirect,
  resetPasswordRoute,
  userInvitationRoute,
  userMailboxOAuthFinish,
  workspaceInitializeRoute,
  workspaceRedirectRoute,
} from '@dms/routes';
import {CommonSkeleton} from '@dms/shared';

import {PersistGate} from 'features/rtkq-persist';

import {AuthBootstrap} from './components/AuthBootstrap/AuthBootstrap';
import {Initialize} from './components/Initialize/Initialize';
import {ModulesContainer} from './components/ModulesContainer/ModulesContainer';
import {OfflineDetector} from './components/OfflineDetector/OfflineDetector';
import {WorkspaceInit} from './components/WorkspaceInit/WorkspaceInit';
import {GlobalStyles} from './GlobalStyles';
import {Doc360Initialize} from './pages/Doc360Initialize/Doc360Initialize';
import {Doc360Redirect} from './pages/Doc360Redirect/Doc360Redirect';
import {Doc360WorkspaceSelection} from './pages/Doc360WorkspaceSelection/Doc360WorkspaceSelection';
import {IamRedirect} from './pages/IamRedirect/IamRedirect';
import {MicrosoftOAuthFinish} from './pages/MicrosoftOAuthFinish/MicrosoftOAuthFinish';
import {MicrosoftOAuthRedirect} from './pages/MicrosoftOAuthRedirect/MicrosoftOAuthRedirect';
import {ResetPasswordRedirect} from './pages/ResetPasswordRedirect/ResetPasswordRedirect';
import {UserInvitationRedirect} from './pages/UserInvitationRedirect/UserInvitationRedirect';
import {UserMailboxOAuthFinish} from './pages/UserMailboxOAuthFinish/UserMailboxOAuthFinish';
import {WorkspaceInitialize, WorkspaceRedirect} from './pages/WorkspaceRedirect/WorkspaceRedirect';
import {reducersDefinitions} from './store/reducers';
import {store} from './store/store';
import './tailwind.css';
import {ComponentsProviderWrapper} from './utils/ComponentsProviderWrapper';

const APPLICATION_NAME = 'OMNETIC';

const router = createBrowserRouter([
  {
    element: (
      <Provider store={store}>
        <StyleSheetManager shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
          <DynamicUiProvider>
            <HelmetProvider>
              <Helmet
                titleTemplate={`%s • ${APPLICATION_NAME}`}
                defaultTitle={APPLICATION_NAME}
                htmlAttributes={{lang: i18n.language}}
              >
                <meta name="description" content={APPLICATION_NAME} />
              </Helmet>
              <ComponentsProviderWrapper>
                <PersistGate
                  reducers={reducersDefinitions}
                  loadingComponent={
                    <Box height="100vh">
                      <CommonSkeleton />
                    </Box>
                  }
                >
                  <Outlet />
                  <OfflineDetector />
                  <GlobalStyles />
                </PersistGate>
              </ComponentsProviderWrapper>
            </HelmetProvider>
          </DynamicUiProvider>
        </StyleSheetManager>
      </Provider>
    ),
    children: createRoutesFromElements(
      <Route element={<WorkspaceInit />} id="WorkspaceInit">
        <Route path={iamRedirectRoute} element={<IamRedirect />} />

        <Route path={doc360Routes.redirect} element={<Doc360Redirect />} />
        <Route path={doc360Routes.workspaceSelection} element={<Doc360WorkspaceSelection />} />
        <Route path={doc360Routes.initialize} element={<Doc360Initialize />} />

        <Route path={workspaceInitializeRoute} element={<WorkspaceInitialize />} />
        <Route path={workspaceRedirectRoute} element={<WorkspaceRedirect />} />

        <Route path={resetPasswordRoute} element={<ResetPasswordRedirect />} />
        <Route path={userInvitationRoute} element={<UserInvitationRedirect />} />

        {/* 
          microsoft redirects to MicrosoftOAuthRedirect without workspace, 
          to call any DMS apis we need to redirect to origin with workspace (MicrosoftOAuthFinish)
          where the OAuth login is finished 
        */}
        <Route path={microsoftOAuthRedirect} element={<MicrosoftOAuthRedirect />} />
        <Route path={microsoftOAuthFinish} element={<MicrosoftOAuthFinish />} />
        <Route path={userMailboxOAuthFinish} element={<UserMailboxOAuthFinish />} />

        <Route element={<AuthBootstrap />}>
          <Route element={<Initialize />} id="init">
            <Route path="*" element={<ModulesContainer />} />
          </Route>
        </Route>
      </Route>
    ),
  },
]);

export function App() {
  return (
    <NuqsAdapter>
      <RouterProvider router={router} useTransitions={false} />
    </NuqsAdapter>
  );
}

/**
 * A function to determine whether a prop should be forwarded to the underlying DOM element.
 * Default behavior was removed in styled-components v6, so we need to provide our own implementation.
 * @see https://styled-components.com/docs/faqs#shouldforwardprop-is-no-longer-provided-by-default
 */
function shouldForwardProp(propName: string, target: unknown) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName);
  }
  // For other elements, forward all props
  return true;
}
